import { useCallback, useState } from 'react';
import type { LucideIcon } from 'lucide-react';
import type { TranslationKey } from '@/core/i18n';

/* =============================================================================
 * Which modules sit on the quick bar
 * -----------------------------------------------------------------------------
 * Stored on the device, beside the theme and the light/dark choice, for the same
 * reason those are: it is a preference about this phone rather than a fact about
 * a person, and putting it on the server would make how somebody works into a
 * record somebody else can read.
 * ========================================================================== */

export interface NavItem {
  to: string;
  /* A key, not a word.
     -------------------------------------------------------------------------
     What identifies a nav item is its route and the permission that reveals it;
     what it is called is a property of the reader. Holding the English here
     would mean the quick-nav bar somebody customised stores English labels in
     their browser, and keeps showing them after they switch language. */
  labelKey: TranslationKey;
  icon: LucideIcon;
  permission: string | null;
}

const KEY = 'noorone.quicknav';

/** Five fits a thumb across a phone. Six is tight; seven is a scroll. */
export const MAX_QUICK = 6;
const MIN_QUICK = 2;

function readStored(): string[] | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed.map(String);
  } catch {
    /* storage unavailable, or something that is not a list */
  }
  return null;
}

export function useQuickNav(available: NavItem[]) {
  const [routes, setRoutes] = useState<string[] | null>(readStored);

  const save = useCallback((next: string[]) => {
    setRoutes(next);
    try {
      localStorage.setItem(KEY, JSON.stringify(next));
    } catch {
      /* the bar still works for this session */
    }
  }, []);

  const clear = useCallback(() => {
    setRoutes(null);
    try {
      localStorage.removeItem(KEY);
    } catch {
      /* ignore */
    }
  }, []);

  /* Filtered against what this person may actually open, every time.
     A stored choice outlives a change of role — somebody who loses payroll
     access should not keep a Payroll tab that refuses them, and somebody who
     gains one should not have to notice. */
  const chosen = routes === null
    ? available.slice(0, 5)
    : routes
      .map((to) => available.find((item) => item.to === to))
      .filter((item): item is NavItem => Boolean(item))
      .slice(0, MAX_QUICK);

  return {
    chosen,
    /** Null while they are following the default, which the picker says out loud. */
    routes,
    save,
    clear,
    canSave: (next: string[]) => next.length >= MIN_QUICK && next.length <= MAX_QUICK,
  };
}
